import { useState, useContext } from 'react';
import { supabase } from '../supabaseClient';
import {
  FaEnvelope,
  FaLock,
  FaSignInAlt,
  FaUserPlus,
  FaUser,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaMapMarkedAlt,
  FaCloudSun,
  FaMicrochip,
  FaClipboardCheck,
  FaCheckCircle,
} from 'react-icons/fa';
import { ThemeContext } from '../App';
import { getDeviceFingerprint, getDeviceName, getLimiteDispositivos, selecionarDispositivosParaSubstituir } from '../utils/deviceFingerprint';
import './Auth.css';

export default function Auth({ notice = '' }) {
  const { darkMode } = useContext(ThemeContext);
  const [isSignUp, setIsSignUp] = useState(false);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);

  // Validações
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password);
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '#ccc' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength: 33, label: 'Fraca', color: '#f44336' };
    if (strength <= 3) return { strength: 66, label: 'Média', color: '#ff9800' };
    return { strength: 100, label: 'Forte', color: '#4caf50' };
  };

  const passwordStrength = getPasswordStrength(password);

  const handleLogin = async () => {
    setLoading(true);
    setMessage(null);
    setError(null);
    setFieldErrors({});

    // Validações
    const errors = {};
    if (!email) errors.email = 'Email é obrigatório';
    else if (!validateEmail(email)) errors.email = 'Email inválido';
    if (!password) errors.password = 'Senha é obrigatória';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setLoading(false);
      return;
    }

    const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setLoading(false);
      if (error.message.toLowerCase().includes('email not confirmed')) {
        setError('Seu email ainda não foi confirmado. Verifique sua caixa de entrada (e pasta de spam) e clique no link de confirmação.');
        setShowResendConfirmation(true);
      } else if (error.message.includes('Invalid login credentials')) {
        setError('Email ou senha incorretos. Se você acabou de criar sua conta, verifique se confirmou o email (incluindo a pasta de spam).');
        // Não exibe botão de reenvio: pode ser senha errada ou conta inexistente
      } else {
        setError('Erro ao entrar. Tente novamente.');
      }
      return;
    }

    const user = signInData.user;

    // ── Verificação de dispositivos ──────────────────────────────────────────
    const fingerprint = getDeviceFingerprint();
    const deviceName = getDeviceName();

    // Busca o perfil antes de registrar recursos que dependem dele por chave
    // estrangeira. Contas criadas por convite ou pelo painel administrativo
    // podem ainda não ter a linha correspondente em public.usuarios.
    let { data: usuarioDb, error: usuarioError } = await supabase
      .from('usuarios')
      .select('id, nome, plano, account_status')
      .eq('id', user.id)
      .single();

    if (usuarioError && (['42703', 'PGRST204'].includes(usuarioError.code) || /account_status/i.test(usuarioError.message || ''))) {
      const legacyProfile = await supabase
        .from('usuarios')
        .select('id, nome, plano')
        .eq('id', user.id)
        .single();
      usuarioDb = legacyProfile.data ? { ...legacyProfile.data, account_status: 'active' } : null;
      usuarioError = legacyProfile.error;
    }

    if (usuarioError && usuarioError.code !== 'PGRST116') {
      await supabase.auth.signOut();
      setLoading(false);
      setError('Não foi possível preparar seu perfil. Tente entrar novamente.');
      return;
    }

    if (!usuarioDb) {
      const nomeMetadata = user.user_metadata?.nome || user.user_metadata?.full_name || '';
      const { data: novoUsuario, error: perfilError } = await supabase
        .from('usuarios')
        .upsert(
          {
            id: user.id,
            email: user.email,
            nome: nomeMetadata,
            telefone: '',
            data_nascimento: null,
          },
          { onConflict: 'id', ignoreDuplicates: true }
        )
        .select('id, nome, plano')
        .single();

      if (perfilError || !novoUsuario) {
        await supabase.auth.signOut();
        setLoading(false);
        setError('Não foi possível criar seu perfil com segurança. Tente novamente.');
        return;
      }

      usuarioDb = novoUsuario;
      usuarioError = null;
    }

    if (usuarioDb?.account_status === 'blocked') {
      await supabase.auth.signOut({ scope: 'local' });
      setLoading(false);
      setError('Esta conta foi bloqueada pelo administrador da plataforma. Fale com o suporte para reativar o acesso.');
      return;
    }

    const plano = usuarioDb?.plano || 'starter';
    const limiteDispositivos = getLimiteDispositivos(plano);

    // Busca dispositivos já cadastrados
    const { data: dispositivos } = await supabase
      .from('dispositivos_usuario')
      .select('id, device_fingerprint, device_name, criado_em, ultimo_acesso')
      .eq('user_id', user.id)
      .order('criado_em', { ascending: true });

    const dispositivoAtual = dispositivos?.find(d => d.device_fingerprint === fingerprint);
    let avisoSubstituicao = '';

    if (!dispositivoAtual) {
      // Limite atingido: libera a vaga do dispositivo com acesso mais antigo.
      const substituidos = selecionarDispositivosParaSubstituir(dispositivos, limiteDispositivos);
      if (substituidos.length > 0) {
        const { error: erroSubstituicao } = await supabase
          .from('dispositivos_usuario')
          .delete()
          .in('id', substituidos.map(d => d.id));
        if (erroSubstituicao) {
          await supabase.auth.signOut();
          setLoading(false);
          setError('Não foi possível liberar uma vaga de dispositivo. Tente novamente.');
          return;
        }
        const nomes = substituidos.map(d => d.device_name || 'Dispositivo').join(', ');
        avisoSubstituicao = ` Pelo limite de dispositivos do plano, o acesso mais antigo (${nomes}) foi desconectado.`;
      }
      // Registra novo dispositivo
      await supabase.from('dispositivos_usuario').insert({
        user_id: user.id,
        device_fingerprint: fingerprint,
        device_name: deviceName,
      });
    } else {
      // Atualiza último acesso e nome do dispositivo existente
      await supabase
        .from('dispositivos_usuario')
        .update({ ultimo_acesso: new Date().toISOString(), device_name: deviceName })
        .eq('id', dispositivoAtual.id);
    }

    // Mantém o nome dos metadados quando o perfil ainda não o possui.
    if (!usuarioDb.nome) {
      const nomeMetadata = user.user_metadata?.nome || '';
      if (nomeMetadata) {
        await supabase.from('usuarios').update({ nome: nomeMetadata }).eq('id', user.id);
      }
    }

    // Registrar log de acesso
    await supabase.from('log_acessos').insert([{
      user_id: user.id,
      email: user.email,
      data_hora: new Date(),
    }]);

    setLoading(false);
    setMessage(`Login realizado com sucesso!${avisoSubstituicao}`);
  };

  const handleSignUp = async () => {
    setLoading(true);
    setMessage(null);
    setError(null);
    setFieldErrors({});

    // Validações
    const errors = {};
    if (!nome || nome.trim().length < 3) errors.nome = 'Nome deve ter pelo menos 3 caracteres';
    if (!email) errors.email = 'Email é obrigatório';
    else if (!validateEmail(email)) errors.email = 'Email inválido';
    if (!password) errors.password = 'Senha é obrigatória';
    else if (!validatePassword(password)) {
      errors.password = 'Senha deve ter 8+ caracteres, 1 maiúscula e 1 número';
    }
    if (!confirmPassword) errors.confirmPassword = 'Confirme sua senha';
    else if (password !== confirmPassword) errors.confirmPassword = 'As senhas não coincidem';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setLoading(false);
      return;
    }

    // Detecta se está rodando no app nativo (Capacitor.isNativePlatform = false no browser web)
    const isCapacitor = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    // Usa a URL de produção para que o link do email funcione em qualquer dispositivo
    const redirectUrl = isCapacitor 
      ? 'com.forecastagro.app://' 
      : (process.env.REACT_APP_SITE_URL || window.location.origin);

    const { data, error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { nome: nome.trim() } // salvo nos metadados como fallback
      }
    });
    setLoading(false);
    
    if (error) {
      console.error('❌ Erro no cadastro:', error);
      if (error.message.includes('already registered') || error.message.includes('User already registered')) {
        setError('Este email já está cadastrado');
      } else if (error.status === 429 || error.message.includes('rate limit') || error.message.includes('too many')) {
        setError('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
      } else if (error.status >= 500) {
        setError(`Erro no servidor (${error.status}). Verifique sua conexão e tente novamente.`);
        console.error('Detalhe do erro 500:', error.message, error);
      } else {
        setError(`Erro ao cadastrar: ${error.message}`);
      }
    } else {
      const user = data?.user;
      // Supabase v2: retorna sucesso mesmo para emails já cadastrados (anti-enumeração).
      // Quando o email já existe, data.user.identities é um array vazio.
      if (user?.identities !== undefined && user.identities.length === 0) {
        setError('Este email já está cadastrado. Faça login ou use "Esqueci minha senha" para recuperar o acesso.');
        setLoading(false);
        return;
      }
      if (user) {
        // upsert: funciona mesmo quando confirmação de e-mail está ativa
        // (sem sessão, o insert pode falhar por RLS; o upsert tenta criar ou atualizar)
        await supabase.from('usuarios').upsert(
          {
            id: user.id,
            email: user.email,
            nome: nome.trim(),
            telefone: '',
            data_nascimento: null
          },
          { onConflict: 'id' }
        );
      }
      setMessage('Cadastro realizado! Verifique seu email para confirmar.');
      // Limpa o formulário
      setNome('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setIsSignUp(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      setError('Digite seu email para reenviar a confirmação.');
      return;
    }
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    setLoading(false);
    if (error) {
      setError('Erro ao reenviar email: ' + error.message);
    } else {
      setMessage('📧 Email de confirmação reenviado! Verifique sua caixa de entrada e spam.');
      setShowResendConfirmation(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Digite seu email para recuperar a senha');
      return;
    }
    if (!validateEmail(email)) {
      setError('Email inválido');
      return;
    }

    setLoading(true);
    setError(null);
    
    // Sempre usa a URL de produção para recuperação de senha.
    // Deep links (com.forecastagro.app://) não funcionam em clientes de email.
    const redirectUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    setLoading(false);

    if (error) {
      setError('Erro ao enviar email de recuperação');
      console.error('Forgot password error:', error);
    } else {
      setMessage('📧 Email de recuperação enviado! Verifique sua caixa de entrada e spam.');
    }
  };

  const selectMode = (signUpMode) => {
    setIsSignUp(signUpMode);
    setFieldErrors({});
    setError(null);
    setMessage(null);
    setShowResendConfirmation(false);
  };

  return (
    <div className={`auth-page${darkMode ? ' is-dark' : ''}`}>
      <div className="auth-page__glow auth-page__glow--one" />
      <div className="auth-page__glow auth-page__glow--two" />

      <main className="auth-shell">
        <section className="auth-brand-panel" aria-label="Apresentação do ClimoraX">
          <div className="auth-brand-panel__grid" />
          <div className="auth-brand-logo">
            <img src="/logo.png" alt="ClimoraX" />
          </div>

          <div className="auth-brand-content">
            <span className="auth-eyebrow">
              <FaShieldAlt /> Plataforma de gestão agrícola
            </span>
            <h1>Sua operação rural conectada, do mapa ao campo.</h1>
            <p>
              Reúna planejamento, clima, sensores e execução em uma visão clara para decidir com mais segurança.
            </p>

            <div className="auth-feature-grid" aria-label="Recursos da plataforma">
              <div className="auth-feature-card">
                <FaMapMarkedAlt />
                <div>
                  <strong>Gestão territorial</strong>
                  <span>Propriedades e talhões no mapa</span>
                </div>
              </div>
              <div className="auth-feature-card">
                <FaMicrochip />
                <div>
                  <strong>Monitoramento</strong>
                  <span>Estações e sensores conectados</span>
                </div>
              </div>
              <div className="auth-feature-card">
                <FaCloudSun />
                <div>
                  <strong>Clima aplicado</strong>
                  <span>Dados para apoiar a operação</span>
                </div>
              </div>
              <div className="auth-feature-card">
                <FaClipboardCheck />
                <div>
                  <strong>Rastreabilidade</strong>
                  <span>Ordens, despesas e histórico</span>
                </div>
              </div>
            </div>
          </div>

          <div className="auth-trust-note">
            <FaCheckCircle />
            <span>Acesso individual e informações organizadas em um só lugar.</span>
          </div>
        </section>

        <section className="auth-access-panel" aria-label={isSignUp ? 'Criar conta' : 'Entrar no ClimoraX'}>
          <div className="auth-mobile-logo">
            <img src="/logo.png" alt="ClimoraX" />
          </div>

          <div className="auth-access-heading">
            <span className="auth-access-kicker">{isSignUp ? 'Nova conta' : 'Acesso à plataforma'}</span>
            <h2>{isSignUp ? 'Comece sua jornada' : 'Bem-vindo de volta'}</h2>
            <p>
              {isSignUp
                ? 'Informe seus dados para criar seu acesso ao ClimoraX.'
                : 'Entre com suas credenciais para acessar sua operação.'}
            </p>
          </div>

          <div className="auth-mode-switch" role="tablist" aria-label="Escolha a forma de acesso">
            <button
              type="button"
              role="tab"
              aria-selected={!isSignUp}
              className={!isSignUp ? 'is-active' : ''}
              onClick={() => selectMode(false)}
              disabled={loading}
            >
              Entrar
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isSignUp}
              className={isSignUp ? 'is-active' : ''}
              onClick={() => selectMode(true)}
              disabled={loading}
            >
              Criar conta
            </button>
          </div>

          <form
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (isSignUp) handleSignUp();
              else handleLogin();
            }}
            noValidate
          >
            {isSignUp && (
              <div className={`auth-field${fieldErrors.nome ? ' has-error' : ''}`}>
                <label htmlFor="auth-name">Nome completo</label>
                <div className="auth-input-wrap">
                  <FaUser aria-hidden="true" />
                  <input
                    id="auth-name"
                    type="text"
                    placeholder="Como devemos chamar você?"
                    value={nome}
                    onChange={(event) => {
                      setNome(event.target.value);
                      if (fieldErrors.nome) setFieldErrors({ ...fieldErrors, nome: null });
                    }}
                    autoComplete="name"
                    disabled={loading}
                  />
                </div>
                {fieldErrors.nome && <span className="auth-field-error">{fieldErrors.nome}</span>}
              </div>
            )}

            <div className={`auth-field${fieldErrors.email ? ' has-error' : ''}`}>
              <label htmlFor="auth-email">Email</label>
              <div className="auth-input-wrap">
                <FaEnvelope aria-hidden="true" />
                <input
                  id="auth-email"
                  type="email"
                  placeholder="seuemail@empresa.com"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: null });
                  }}
                  autoComplete="email"
                  inputMode="email"
                  disabled={loading}
                />
              </div>
              {fieldErrors.email && <span className="auth-field-error">{fieldErrors.email}</span>}
            </div>

            <div className={`auth-field${fieldErrors.password ? ' has-error' : ''}`}>
              <div className="auth-field-label-row">
                <label htmlFor="auth-password">Senha</label>
                {!isSignUp && (
                  <button type="button" className="auth-text-button" onClick={handleForgotPassword} disabled={loading}>
                    Esqueci minha senha
                  </button>
                )}
              </div>
              <div className="auth-input-wrap">
                <FaLock aria-hidden="true" />
                <input
                  id="auth-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={isSignUp ? 'Mínimo de 8 caracteres' : 'Digite sua senha'}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (fieldErrors.password) setFieldErrors({ ...fieldErrors, password: null });
                  }}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  disabled={loading}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {fieldErrors.password && <span className="auth-field-error">{fieldErrors.password}</span>}
            </div>

            {isSignUp && password && (
              <div className="auth-password-strength" aria-label={`Força da senha: ${passwordStrength.label}`}>
                <div className="auth-password-strength__track">
                  <span style={{ width: `${passwordStrength.strength}%`, background: passwordStrength.color }} />
                </div>
                <span style={{ color: passwordStrength.color }}>Senha {passwordStrength.label}</span>
              </div>
            )}

            {isSignUp && (
              <div className={`auth-field${fieldErrors.confirmPassword ? ' has-error' : ''}`}>
                <label htmlFor="auth-confirm-password">Confirmar senha</label>
                <div className="auth-input-wrap">
                  <FaLock aria-hidden="true" />
                  <input
                    id="auth-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Digite a senha novamente"
                    value={confirmPassword}
                    onChange={(event) => {
                      setConfirmPassword(event.target.value);
                      if (fieldErrors.confirmPassword) setFieldErrors({ ...fieldErrors, confirmPassword: null });
                    }}
                    autoComplete="new-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Ocultar confirmação de senha' : 'Mostrar confirmação de senha'}
                    disabled={loading}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <span className="auth-field-error">{fieldErrors.confirmPassword}</span>
                )}
              </div>
            )}

            <button type="submit" className="auth-primary-button" disabled={loading}>
              {isSignUp ? <FaUserPlus /> : <FaSignInAlt />}
              <span>
                {loading
                  ? (isSignUp ? 'Criando sua conta...' : 'Entrando com segurança...')
                  : (isSignUp ? 'Criar minha conta' : 'Entrar no ClimoraX')}
              </span>
            </button>
          </form>

          {showResendConfirmation && !isSignUp && (
            <button
              type="button"
              className="auth-resend-button"
              onClick={handleResendConfirmation}
              disabled={loading}
            >
              <FaEnvelope /> Reenviar email de confirmação
            </button>
          )}

          {notice && !error && <div className="auth-feedback is-error" role="alert">{notice}</div>}
          {error && <div className="auth-feedback is-error" role="alert">{error}</div>}
          {message && <div className="auth-feedback is-success" role="status">{message}</div>}

          <div className="auth-security-note">
            <FaShieldAlt />
            <span>Seu acesso é protegido por autenticação individual.</span>
          </div>

          <p className="auth-privacy-note">
            Ao continuar, você declara estar de acordo com nossa{' '}
            <a href="/politica-privacidade.html" target="_blank" rel="noreferrer">Política de Privacidade</a>.
          </p>
        </section>
      </main>

      <footer className="auth-footer">ClimoraX · Gestão agrícola conectada</footer>
    </div>
  );
}

